import {
  BoxType,
  type CustomBackgroundTemplateBox,
  type CustomTemplateBox,
  type CustomTemplateBoxRect,
} from '@lite-workspace/dto-fotoclass-templates';
import { BookData } from '../interfaces/book-data.interface';
import { CollageData } from '../interfaces/collage-data.interface';
import { DrawableBackgroundData, DrawableData, isDrawableBackground } from '../interfaces/drawable-data.interface';
import { PrintSizes } from '../interfaces/print-sizes.interface';
import { RectangleData } from '../interfaces/rectangle-data.interface';
import { SpreadData } from '../interfaces/spread-data.interface';
import { tryRectFromBox } from '../normalizer/boxes/rect';
import { detectSchemaVersion } from '../normalizer/schema-detect';
import type { PersistedBookModelV1, PersistedCollageV1, PersistedSpreadV1 } from '../normalizer/types';
import { getPrintSizes } from '../queries/book-queries';

// Потерянный вылет — единицы процентов холста; что мельче — декоративная плашка, что заметно крупнее — заливка чужого холста.
const FILL_COVERAGE = 0.9;
const FILL_OVERSHOOT = 2 - FILL_COVERAGE;
// Зазор от края холста, ещё похожий на потерянный вылет: намеренная симметричная рамка с полем
// ~5% остаётся заливкой, а контейнер, сдвинутый от центра/края дальше допуска, — расстановка.
const FILL_EDGE_TOLERANCE = 0.05;

function axisFills(start: number, size: number, canvasStart: number, canvasSize: number): boolean {
  // Ось, перекрывающая холст с запасом, — заливка при любом сдвиге: сдвинутый полноразмерный фон тоже теряет вылет.
  if (size >= canvasSize) return true;

  const tolerance = canvasSize * FILL_EDGE_TOLERANCE;
  return Math.abs(start - canvasStart) <= tolerance && Math.abs(canvasStart + canvasSize - (start + size)) <= tolerance;
}

function isFillOf(container: RectangleData, left: number, top: number, width: number, height: number): boolean {
  const inSizeWindow =
    container.width >= width * FILL_COVERAGE &&
    container.height >= height * FILL_COVERAGE &&
    container.width <= width * FILL_OVERSHOOT &&
    container.height <= height * FILL_OVERSHOOT;

  return (
    inSizeWindow &&
    axisFills(container.left, container.width, left, width) &&
    axisFills(container.top, container.height, top, height)
  );
}

// Постраничная заливка хранится половинными боксами в координатах разворота — её холст это своя половина.
function fillRegion(container: RectangleData, width: number, height: number): RectangleData | null {
  if (isFillOf(container, 0, 0, width, height)) return { left: 0, top: 0, width, height, angle: 0 };

  const pageWidth = width / 2;
  const onRightPage = container.left + container.width / 2 >= pageWidth;
  const pageLeft = onRightPage ? pageWidth : 0;
  if (!isFillOf(container, pageLeft, 0, pageWidth, height)) return null;

  return { left: pageLeft, top: 0, width: pageWidth, height, angle: 0 };
}

function covers(container: RectangleData, region: RectangleData): boolean {
  return (
    container.left <= region.left &&
    container.top <= region.top &&
    container.left + container.width >= region.left + region.width &&
    container.top + container.height >= region.top + region.height
  );
}

// Пиксели кладём в footprint по большей стороне, а container режет лишнее — из него рендер берёт размер печатного листа.
function coverContent(region: RectangleData, sourceAspect: number | null): RectangleData | null {
  if (!sourceAspect || !isFinite(sourceAspect) || sourceAspect <= 0) return null;

  const regionAspect = region.width / region.height;
  if (Math.abs(sourceAspect - regionAspect) <= regionAspect * 1e-9) return null;

  if (sourceAspect > regionAspect) {
    const covered = region.height * sourceAspect;
    return {
      left: region.left + (region.width - covered) / 2,
      top: region.top,
      width: covered,
      height: region.height,
      angle: 0,
    };
  }

  const covered = region.width / sourceAspect;
  return {
    left: region.left,
    top: region.top + (region.height - covered) / 2,
    width: region.width,
    height: covered,
    angle: 0,
  };
}

function toFullBleed(
  background: DrawableBackgroundData,
  width: number,
  height: number,
  sourceAspect: number | null,
): DrawableBackgroundData {
  // модель приходит из нетипизированного JSON — контейнера может не быть вовсе
  const naturalAligns = background.naturalAligns;
  const container = naturalAligns?.container;
  if (!container) return background;
  // Повёрнутая, подрезанная или поставленная руками заливка — осознанная расстановка, а не потерянный вылет.
  if ((container.angle ?? 0) !== 0 || (background.rotation ?? 0) !== 0 || naturalAligns.content || background.placed) {
    return background;
  }

  const region = fillRegion(container, width, height);
  if (!region || covers(container, region)) return background;

  const content = coverContent(region, sourceAspect);

  return {
    ...background,
    naturalAligns: { ...naturalAligns, container: region, ...(content && { content }) },
  };
}

// Натуральные пропорции фонового ассета: все версии (thumb/medium/large) рендерятся из оригинала
// с сохранением пропорций, так что aspect любой из них — aspect оригинала; крупнейшая ближе всего к нему.
function assetVersionsAspect(versions: DrawableBackgroundData['data']['versions']): number | null {
  const candidate = versions?.large ?? versions?.medium ?? versions?.thumb;
  return candidate && candidate.width > 0 && candidate.height > 0 ? candidate.width / candidate.height : null;
}

function templateAspect(collage: Pick<CollageData, 'originalCustomTemplate'>): number | null {
  const template = collage.originalCustomTemplate as { originalWidth?: number; originalHeight?: number } | null;
  const width = template?.originalWidth;
  const height = template?.originalHeight;

  return width && height ? width / height : null;
}

export function applyFullBleedBackgroundToCollage(
  collage: CollageData,
  width: number,
  height: number,
  sourceAspect: number | null = templateAspect(collage),
): CollageData {
  let changed = false;
  // модель приходит из нетипизированного JSON — списка объектов может не быть вовсе
  const drawableObjects = (collage.drawableObjects ?? []).map((drawable): DrawableData => {
    if (!isDrawableBackground(drawable)) return drawable;
    // Применённый фон пришёл из отдельного пака — пропорции макетного шаблона коллажа к нему не относятся,
    // aspect берём из версий ассета (generateDrawableBackground прокидывает их из box.params).
    // Без него фон не расширяем: растяжение контейнера без content заставляет рендер тянуть оси
    // независимо (raster.rs, content_rect: None) — тот же неравномерный стретч, что у фона разворота.
    // Pack-origin covers both markers: `packApplied` (any pack application) and `userApplied` (explicit
    // pick — the only marker legacy persisted models carry).
    if (drawable.userApplied || drawable.packApplied) {
      const appliedAspect = assetVersionsAspect(drawable.data?.versions);
      if (!appliedAspect) return drawable;
      const stretched = toFullBleed(drawable, width, height, appliedAspect);
      if (stretched !== drawable) changed = true;
      return stretched;
    }

    const stretched = toFullBleed(drawable, width, height, sourceAspect);
    if (stretched !== drawable) changed = true;
    return stretched;
  });

  return changed ? { ...collage, drawableObjects } : collage;
}

// Фон разворота приходит из отдельного пака — пропорции макетного шаблона к нему отношения не имеют,
// реальный aspect берём из версий ассета. Без него фон не расширяем: растяжение контейнера без content
// заставляет рендер (raster.rs, content_rect: None) тянуть оси независимо — неравномерный стретч
// (~2%, в угловом случае до ~20%), а честные поля по краю лучше искажённой заливки.
function spreadBackgroundToFullBleed(background: DrawableBackgroundData, printSizes: PrintSizes): DrawableBackgroundData {
  const sourceAspect = assetVersionsAspect(background.data?.versions);
  if (!sourceAspect) return background;

  return toFullBleed(background, printSizes.width, printSizes.height, sourceAspect);
}

export function applyFullBleedBackground<T extends SpreadData>(spread: T, printSizes: PrintSizes): T {
  const collages = spread.collages ?? [];
  // Коробки коллажа хранятся в координатах своей страницы — горизонтальное смещение добавляет рендер.
  const collageWidth = collages.length > 1 ? printSizes.width / 2 : printSizes.width;

  const background = spread.background ? spreadBackgroundToFullBleed(spread.background, printSizes) : spread.background;
  const nextCollages = collages.map(collage => applyFullBleedBackgroundToCollage(collage, collageWidth, printSizes.height));

  const collagesChanged = nextCollages.some((collage, i) => collage !== collages[i]);
  if (background === spread.background && !collagesChanged) return spread;

  return { ...spread, ...(background ? { background } : {}), ...(collagesChanged && { collages: nextCollages }) };
}

export function applyFullBleedBackgroundToBook(book: BookData): BookData {
  const printSizes = getPrintSizes(book);
  const spreads = book.spreads.map(spread => applyFullBleedBackground(spread, printSizes));

  return spreads.some((spread, i) => spread !== book.spreads[i]) ? { ...book, spreads } : book;
}

// Ключ — текст сообщения, не модель/её ссылка: один и тот же перманентно битый документ иначе слал бы
// warn на каждое чтение/автосейв/синк/Kafka-тач вечно, топя редкий новый сигнал. Кардинальность
// ограничена числом разных причин, не числом документов.
const warnedFullBleedNotices = new Set<string>();

function warnFullBleedOnce(message: string): void {
  if (warnedFullBleedNotices.has(message)) return;
  warnedFullBleedNotices.add(message);
  console.warn(`[photobook-model] ${message}`);
}

// Симметричная нормализация перед сравнением моделей (diff, инвалидация превью, auto-снапшоты):
// клиент сохраняет модель, уже приведённую на чтении, поэтому сравнивать надо обе стороны после
// миграции — иначе одноразовая тихая миграция фонов выглядит правкой модели, а при model-less PATCH
// односторонняя нормализация давала бы ложный modelChanged.
// Ветку выбирает форма самого документа: колонка несёт обе, а мутатор чужой формы — тождество
// (v0 читает `data.versions` и `drawableObjects`, которых у боксов нет), так что без развилки
// инвариант «сравниваем после миграции» на v1-колонке не выполнялся бы вовсе, а гейт вырождался бы
// в сравнение сырья. Идемпотентна; битая модель возвращается как есть.
// Следствие принято: намеренное «отжатие» фона обратно внутрь реза не выживает — на чтении модель
// всё равно нормализуется, так что такую правку клиент физически прислать не может.
export function normalizeFullBleedBackgrounds<T>(model: T): T {
  if (!model) return model;

  try {
    const detected = detectSchemaVersion(model);
    // Смешанный документ (ручной хотфикс) развилка чинить не берётся, но и молчать о нём не должна.
    for (const issue of detected.issues) warnFullBleedOnce(`full-bleed normalization: ${issue.path} — ${issue.reason}`);

    return detected.schemaVersion === 'custom-template-v1'
      ? (applyFullBleedBackgroundToBookBoxes(model as unknown as PersistedBookModelV1) as unknown as T)
      : (applyFullBleedBackgroundToBook(model as unknown as BookData) as unknown as T);
  } catch (error) {
    // Битая модель не должна ронять сравнение, но и исчезать бесшумно не должна —
    // два потребителя (diff при PATCH, auto-снапшоты) иначе не узнают, что сравнивали сырьё.
    const reason = error instanceof Error ? error.message : String(error);
    warnFullBleedOnce(`full-bleed normalization skipped a broken model: ${reason}`);

    return model;
  }
}

// ─── v1-твины full-bleed машинерии (custom-template-v1, PersistedBookModelV1) ────────────────────
// Пороги и геометрия общие с v0-веткой выше — расходится только форма дерева (`boxes` вместо
// `drawableObjects`) и адрес полей. Угол у v1 один: `box.rect` его не несёт (всегда 0), весь поворот
// живёт в `box.rotation`, поэтому здесь одно сравнение там, где у v0 их два.
// Две ветки — состояние перехода, а не целевая форма: v0-ветку держат живые вызывающие, подающие уже
// декодированный BookData, и она уходит из файла вместе с последним из них.

function rectToTuple(rect: RectangleData): CustomTemplateBoxRect {
  return [rect.left, rect.top, rect.width, rect.height];
}

function toFullBleedBox(
  box: CustomBackgroundTemplateBox,
  width: number,
  height: number,
  sourceAspect: number | null,
): CustomBackgroundTemplateBox {
  // документ приходит из нетипизированного JSON — прямоугольника может не быть вовсе
  const container = tryRectFromBox(box.rect);
  if (!container) return box;
  // Повёрнутая, подрезанная или поставленная руками заливка — осознанная расстановка, а не потерянный вылет.
  if ((box.rotation ?? 0) !== 0 || box.contentRect || box.params?.placed) return box;

  const region = fillRegion(container, width, height);
  if (!region || covers(container, region)) return box;

  const content = coverContent(region, sourceAspect);

  return { ...box, rect: rectToTuple(region), ...(content && { contentRect: rectToTuple(content) }) };
}

/** v1-твин applyFullBleedBackgroundToCollage: фоны-декор внутри коллажа, в координатах своего холста. */
export function applyFullBleedBackgroundToCollageBoxes(
  collage: PersistedCollageV1,
  width: number,
  height: number,
  sourceAspect: number | null = templateAspect(collage),
): PersistedCollageV1 {
  let changed = false;
  // Вложенные в группы фоны не трогаем — v0 так же не спускается в детей группы, а заливка листа там не живёт.
  // Легаси-коллаж внутри v1-книги приходит сюда без `boxes` и остаётся без миграции сознательно:
  // смешанный документ чинят ручной конвертацией, а не полукнижным бейком; о самой смеси предупреждает
  // развилка в normalizeFullBleedBackgrounds.
  const boxes = (collage.boxes ?? []).map((box): CustomTemplateBox => {
    if (box.type !== BoxType.BACKGROUND) return box;
    // Применённый фон пришёл из отдельного пака — пропорции макетного шаблона коллажа к нему не относятся.
    if (box.params?.userApplied || box.params?.packApplied) {
      const appliedAspect = assetVersionsAspect(box.params.versions);
      if (!appliedAspect) return box;
      const stretched = toFullBleedBox(box, width, height, appliedAspect);
      if (stretched !== box) changed = true;
      return stretched;
    }

    const stretched = toFullBleedBox(box, width, height, sourceAspect);
    if (stretched !== box) changed = true;
    return stretched;
  });

  return changed ? { ...collage, boxes } : collage;
}

function spreadBackgroundToFullBleedBox(
  background: CustomBackgroundTemplateBox,
  printSizes: PrintSizes,
): CustomBackgroundTemplateBox {
  const sourceAspect = assetVersionsAspect(background.params?.versions);
  if (!sourceAspect) return background;

  return toFullBleedBox(background, printSizes.width, printSizes.height, sourceAspect);
}

/** v1-твин applyFullBleedBackground: фон разворота — first-class бокс развёрнутого спреда, фоны коллажей — его боксы. */
export function applyFullBleedBackgroundBoxes<T extends PersistedSpreadV1>(spread: T, printSizes: PrintSizes): T {
  const collages = spread.collages ?? [];
  // Коробки коллажа хранятся в координатах своей страницы — горизонтальное смещение добавляет рендер.
  const collageWidth = collages.length > 1 ? printSizes.width / 2 : printSizes.width;

  const background = spread.background ? spreadBackgroundToFullBleedBox(spread.background, printSizes) : spread.background;
  const nextCollages = collages.map(collage => applyFullBleedBackgroundToCollageBoxes(collage, collageWidth, printSizes.height));

  const collagesChanged = nextCollages.some((collage, i) => collage !== collages[i]);
  if (background === spread.background && !collagesChanged) return spread;

  return { ...spread, ...(background ? { background } : {}), ...(collagesChanged && { collages: nextCollages }) };
}

/** v1-твин applyFullBleedBackgroundToBook. */
export function applyFullBleedBackgroundToBookBoxes(book: PersistedBookModelV1): PersistedBookModelV1 {
  const printSizes = getPrintSizes(book);
  const spreads = book.spreads.map(spread => applyFullBleedBackgroundBoxes(spread, printSizes));

  return spreads.some((spread, i) => spread !== book.spreads[i]) ? { ...book, spreads } : book;
}
